/**
 * RelatedTopicChip — a Link pill that reveals a lightweight preview
 * tooltip on hover / focus. First hover triggers a background
 * `GET /api/ai-topics/{slug}` so the user sees the article's first
 * sentence + tags without leaving the page. If the target doesn't
 * exist yet, tooltip shows "Not generated yet — click to create."
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { slugifyTopic } from "@/hooks/useAiTopic";

function firstSentence(body) {
  const s = String(body || "").trim();
  if (!s) return "";
  const m = s.match(/[^.!?]+[.!?]/);
  return (m ? m[0] : s).slice(0, 220);
}

export default function RelatedTopicChip({ label, source = "generic" }) {
  const slugBase = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 100);
  const targetSlug = slugifyTopic(source, slugBase);
  const [preview, setPreview] = useState(null);      // {title, snippet, tags[], status} | null
  const [state, setState] = useState("idle");         // idle | loading | done | missing

  const fetchPreview = async () => {
    if (state !== "idle") return;
    setState("loading");
    try {
      const r = await http.get(`/ai-topics/${targetSlug}`);
      setPreview({
        title:   r.data?.title,
        snippet: firstSentence(r.data?.body),
        tags:    (r.data?.tags || []).slice(0, 4),
        status:  r.data?.status,
      });
      setState("done");
    } catch (e) {
      setState("missing");
    }
  };

  return (
    <span
      className="relative inline-flex group"
      onMouseEnter={fetchPreview}
      onFocus={fetchPreview}
    >
      <Link
        to={`/${source === "generic" ? "topic" : source + "/topic"}/${slugBase}`}
        className="inline-flex items-center gap-1 text-sm rounded-full border px-3 py-1.5 hover:bg-black/[0.04] transition"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid={`ai-topic-related-${slugBase}`}
      >
        {label}
      </Link>
      {(state === "loading" || state === "done" || state === "missing") && (
        <span
          className="pointer-events-none absolute left-0 top-full mt-2 w-72 z-30
                     opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
                     transition"
          data-testid={`ai-topic-related-preview-${slugBase}`}
        >
          <span
            className="block rounded-lg border shadow-lg p-3 text-xs"
            style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
          >
            {state === "loading" && <span className="opacity-70">Loading preview…</span>}
            {state === "done" && preview?.status === "ready" && (
              <>
                <span className="block font-medium mb-1 text-[13px]">{preview.title}</span>
                <span className="block opacity-80 leading-snug">{preview.snippet}</span>
                {preview.tags?.length > 0 && (
                  <span className="mt-2 flex flex-wrap gap-1">
                    {preview.tags.map((t) => (
                      <span key={t} className="text-[10px] rounded-full border px-1.5 py-0.5 opacity-70"
                            style={{ borderColor: "var(--wofy-border)" }}>{t}</span>
                    ))}
                  </span>
                )}
              </>
            )}
            {state === "done" && preview?.status !== "ready" && (
              <span className="opacity-70">Generating this article now — click to open.</span>
            )}
            {state === "missing" && (
              <span className="opacity-70">Not generated yet — click to create.</span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}
